import { useEffect, useState } from 'react';
import { ExperimentOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useChayThuBo } from '../hooks/useThamDinh';
import { ChonTepHoSo, DUOI_NHAN, TRAN_MOI_TEP } from './ChonTepHoSo';

export interface HopChayThuProps {
  open: boolean;
  onClose: () => void;
  boId: string;
  boTen: string;
  /** Mở màn chi tiết của lượt chạy thử để theo dõi tiến trình và xem kết quả. */
  onXong?: (phienId: string) => void;
}

/**
 * Chạy thử bộ tiêu chí trên một hồ sơ mẫu (đặc tả mục 13.3, Mốc 9).
 *
 * Hộp nói rõ hai điều mà người soạn hiểu sai thì mất thời gian:
 *
 * 1. **Chấm theo bản nháp**, không theo phiên bản đã phát hành — đó là mục đích, nhưng nó cũng nghĩa là kết
 *    quả này không nói gì về hồ sơ thật đang chấm theo bản cũ.
 * 2. **Lượt chạy thử không vào danh sách hồ sơ** — nếu không nói thì người soạn sẽ đi tìm nó ở đó.
 *
 * Backend chỉ giữ **một** lượt mỗi bộ: chạy lần nữa là xoá lượt cũ. Hộp không cần cảnh báo chuyện đó vì lượt
 * cũ chấm theo một bản nháp đã khác, nên giữ nó cũng không dùng được.
 */
export function HopChayThu({ open, onClose, boId, boTen, onXong }: HopChayThuProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const chay = useChayThuBo();

  const [tieuDe, setTieuDe] = useState('');
  const [tep, setTep] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;
    setTieuDe(t('thamDinh.soan.chayThu'));
    setTep([]);
    chay.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, boId]);

  const loiTruong = loiTheoTruong(chay.error);
  const loiChung = chay.error && Object.keys(loiTruong).length === 0 ? dichLoi(chay.error) : undefined;
  const quaLon = tep.some((f) => f.size > TRAN_MOI_TEP);

  return (
    <Dialog open={open} onClose={onClose} title={t('thamDinh.soan.chayThuTieuDe')}>
      <div className="space-y-4 pt-2">
        <p className="text-sm text-content-muted">{t('thamDinh.soan.chayThuMo')}</p>
        <p className="truncate text-sm font-medium">{boTen}</p>

        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        <Field label={t('thamDinh.nop.tieuDeHoSo')} error={loiTruong['/tieu_de']}>
          {(p) => <Input {...p} value={tieuDe} onChange={(e) => setTieuDe(e.target.value)} />}
        </Field>

        <Field
          label={t('thamDinh.soan.chayThuChonTep')}
          required
          hint={t('thamDinh.nop.tepMo', { duoi: DUOI_NHAN.join(', ') })}
        >
          {() => <ChonTepHoSo tep={tep} onDoi={setTep} nhan={t('thamDinh.soan.chayThuChonTep')} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>{t('chung.huy')}</Button>
          <Button
            variant="primary"
            size="md"
            icon={<ExperimentOutlined />}
            loading={chay.isPending}
            disabled={tep.length === 0 || quaLon}
            onClick={() => chay.mutate({ boId, tieuDe: tieuDe.trim(), tep },
              { onSuccess: (ph) => { onClose(); onXong?.(ph.id); } })}
          >
            {t('thamDinh.soan.chayThuBatDau')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
